import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import LoadingProgress from '../components/LoadingProgress'

import {
  fetchIncidences,
  fetchIncidence,
  fetchIncidencesCSV,
  reviewIncidence,
  repairIncidence,
  appendRepairDetail,
  resolveIncidence
} from '../actions/incidences'

import ErrorMessage from '../components/ErrorMessage'
import IncidencesFilterForm from '../forms/IncidencesFilterForm'

import '../styles/IncidencesAdminView.css'

const STATUS_LABELS = {
  OPEN: 'Abiertas',
  IN_REVIEW: 'En Revisión',
  IN_REPAIR: 'En Reparación',
  REPAIR: 'En Reparación',
  RESOLVED: 'Resueltas',
  CLOSED: 'Cerradas'
}

const CATEGORY_LABELS = {
  Minor: 'Menor',
  Breakdown: 'Avería',
  UrgentBreakdown: 'Avería Urgente',
  COINS: 'Pago Monedas',
  BILLS: 'Pago Billetes',
  TPV: 'Pago Tarjeta',
  SCREEN_OFF: 'Pantalla Apagada',
  SCREEN_BREAK: 'Pantalla Rota',
  SCREEN_TOUCH: 'Táctil No Responde',
  PRINTER_TICKET: 'Impresora Ticket',
  KIOSC_DOOR: 'Kiosco Puerta',
  KIOSC_OFF: 'Kiosco No Enciende',
  OTHER: 'Otro',
}

const PRIORITY_LABELS = {
  LOW: 'Consulta general',
  MEDIUM: 'Incidente menor',
  HIGH: 'Avería',
  CRITICAL: 'Avería urgente',
}

const FILTER_TABS = [
  { key: 'ALL', label: 'Todas' },
  { key: 'OPEN', label: 'Abiertas' },
  { key: 'IN_REVIEW', label: 'En Revisión' },
  { key: 'IN_REPAIR', label: 'En Reparación' },
  { key: 'RESOLVED', label: 'Resueltas' },
]

class IncidencesView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      activeFilter: 'ALL',
      detailModalOpen: false,
      detailNumber: null,
      actuationText: '',
      showActuationModal: false,
      actuationAction: null, // 'repair' or 'resolve'
      now: new Date(), // for live time elapsed updates
    }

    this.handleIncidencesCSV = this.handleIncidencesCSV.bind(this)
    this.closeDetailModal = this.closeDetailModal.bind(this)
    this.handleActuationSubmit = this.handleActuationSubmit.bind(this)
  }

  componentDidMount() {
    this.props.fetchIncidences('', '', '', '', '', '')
    // fetch SLAs once for client-side comparisons (avoid populating SLA per incidence)
    if (this.props.fetchSLAs) this.props.fetchSLAs()
    // Update time every 60 seconds for live elapsed time
    this._timer = setInterval(() => this.setState({ now: new Date() }), 60000)
  }

  componentWillUnmount() {
    if (this._timer) clearInterval(this._timer)
  }

  componentDidUpdate(prevProps) {
    if (this.props.incidenceNeedRefresh && !prevProps.incidenceNeedRefresh) {
      this.props.fetchIncidences('', '', '', '', '', '')
      // also refresh the open detail if any
      if (this.state.detailNumber) {
        this.props.fetchIncidence(this.state.detailNumber)
      }
    }
  }

  goTo(number) {
    this.setState({ detailModalOpen: true, detailNumber: number })
    this.props.fetchIncidence(number)
  }

  closeDetailModal() {
    this.setState({ detailModalOpen: false, detailNumber: null, actuationText: '' })
  }

  handleReviewIncidence(number, e) {
    if (e) e.stopPropagation()
    this.props.reviewIncidence(number)
  }

  // Open actuation modal for repair action
  handleOpenRepairModal(number, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'repair',
      actuationText: '',
      detailNumber: number
    })
  }

  // Open actuation modal to append a repair note (for IN_REPAIR incidences)
  handleOpenAppendRepairModal(number, e) {
    if (e) e.stopPropagation()
    this.setState({
      showActuationModal: true,
      actuationAction: 'append_repair',
      actuationText: '',
      detailNumber: number
    })
  }

  // Open actuation modal for resolve action
  handleOpenResolveModal(number, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'resolve',
      actuationText: '',
      detailNumber: number
    })
  }

  handleActuationSubmit() {
    const { actuationAction, actuationText, detailNumber } = this.state
    
    if (!actuationText.trim()) {
      alert('Por favor, introduce el texto de actuación')
      return
    }

    if (actuationAction === 'repair') {
      this.props.repairIncidence(detailNumber, actuationText)
    } else if (actuationAction === 'append_repair') {
      this.props.appendRepairDetail(detailNumber, actuationText)
    } else if (actuationAction === 'resolve') {
      this.props.resolveIncidence(detailNumber, actuationText)
    }

    this.setState({ 
      showActuationModal: false, 
      actuationAction: null, 
      actuationText: '' 
    })
  }

  handleIncidencesCSV() {
    const { fetchIncidencesCSV, form } = this.props

    fetchIncidencesCSV(
      form.IncidencesFilterForm && form.IncidencesFilterForm.values ? form.IncidencesFilterForm.values.Status : undefined,
      form.IncidencesFilterForm && form.IncidencesFilterForm.values ? form.IncidencesFilterForm.values.Machine : undefined,
      form.IncidencesFilterForm && form.IncidencesFilterForm.values ? form.IncidencesFilterForm.values.StartDate : undefined,
      form.IncidencesFilterForm && form.IncidencesFilterForm.values ? form.IncidencesFilterForm.values.EndDate : undefined
    )
  }

  /**
   * Format a millisecond duration into a human-readable string.
   * Minutes are always rounded UP to the next whole minute (ceil).
   */
  formatDuration(ms) {
    if (ms <= 0) return '0m'
    // round any partial minute up, then split into days/hours/minutes so
    // carries (60 minutes → 1 hour) are handled automatically
    const totalMinutes = Math.ceil(ms / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    let text = ''
    if (days > 0) text += `${days}d `
    if (hours > 0 || days > 0) text += `${hours}h `
    text += `${minutes}m`
    return text.trim()
  }

  /**
   * Get severity class based on elapsed days
   */
  getTimerSeverity(ms) {
    const days = ms / (1000 * 60 * 60 * 24)
    if (days >= 3) return 'critical'
    if (days >= 1) return 'warning'
    return 'normal'
  }

  /**
   * Compute SLA remaining milliseconds for an incidence based on SLA.maxRepairTimeMax.
   * Returns null when SLA or timestamps missing.
   */
  getSlaRemainingMsForIncidence(inc) {
    if (!inc || !this.props.slas || !inc.Customer || !inc.Customer.SLA) return null
    const slaId = inc.Customer.SLA && (inc.Customer.SLA._id || inc.Customer.SLA)
    const slaObj = (this.props.slas || []).find(s => String(s._id) === String(slaId))
    if (!slaObj) return null

    const priorityKey = (inc.Priority || 'MEDIUM').toLowerCase()
    const slaEntry = slaObj[priorityKey]
    const slaMaxRepair = slaEntry && slaEntry.maxRepairTimeMax
    if (typeof slaMaxRepair !== 'number') return null

    // Use the incidence's TOTAL elapsed time (open + review + repair)
    // Reuse the existing timer calculation so the SLA remaining accounts for all workflow phases
    const timers = this.getIncidenceTimers(inc)
    const totalTimer = (timers || []).find(t => t.isTotal)
    if (!totalTimer || typeof totalTimer.ms !== 'number') return null
    const totalMs = Math.max(totalTimer.ms, 0)

    const remainingMs = (slaMaxRepair * 60 * 60 * 1000) - totalMs
    return remainingMs
  }

  /**
   * Calculate time counters for incidence status transitions.
   * Returns an array of { key, icon, label, time, ms, active, isLive, severity }
   * 1. Open time: OPEN → IN_REVIEW (or now if still open)
   * 2. In revision time: IN_REVIEW → IN_REPAIR/RESOLVED (or now if still in review)
   * 3. In repair time: IN_REPAIR → RESOLVED (or now if still in repair)
   * 4. Total: sum of all 3 counters
   */
  getIncidenceTimers(incidence) {
    const now = this.state.now
    const openAt = incidence.createdAt ? new Date(incidence.createdAt) : (incidence.OpenAt ? new Date(incidence.OpenAt) : null)
    const inReviewAt = incidence.InReviewAt ? new Date(incidence.InReviewAt) : null
    const inRepairAt = incidence.InRepairAt ? new Date(incidence.InRepairAt) : null
    const resolvedAt = incidence.ResolvedAt ? new Date(incidence.ResolvedAt) : null

    const timers = []

    // 1) Open Time: OPEN → IN_REVIEW (or now if still open)
    const openEnd = inReviewAt || (incidence.Status === 'OPEN' ? now : null)
    const openMs = openAt && openEnd ? openEnd - openAt : 0
    timers.push({
      key: 'inc_open',
      icon: 'fas fa-folder-open',
      label: 'Abierto',
      time: this.formatDuration(Math.max(openMs, 0)),
      ms: Math.max(openMs, 0),
      active: true,
      isLive: incidence.Status === 'OPEN',
      severity: this.getTimerSeverity(Math.max(openMs, 0)),
    })

    // 2) In Revision Time: IN_REVIEW → IN_REPAIR/RESOLVED (or now if still in review)
    const revisionStart = inReviewAt
    const revisionEnd = inRepairAt || resolvedAt || (incidence.Status === 'IN_REVIEW' ? now : null)
    const revisionMs = revisionStart && revisionEnd ? revisionEnd - revisionStart : 0
    const revisionActive = !!inReviewAt
    timers.push({
      key: 'inc_revision',
      icon: 'fas fa-eye',
      label: 'En Revisión',
      time: this.formatDuration(Math.max(revisionMs, 0)),
      ms: Math.max(revisionMs, 0),
      active: revisionActive,
      isLive: incidence.Status === 'IN_REVIEW',
      severity: revisionActive ? this.getTimerSeverity(Math.max(revisionMs, 0)) : 'normal',
    })

    // 3) In Repair Time: IN_REPAIR → RESOLVED (or now if still in repair)
    const repairStart = inRepairAt
    const repairEnd = resolvedAt || (incidence.Status === 'IN_REPAIR' || incidence.Status === 'REPAIR' ? now : null)
    const repairMs = repairStart && repairEnd ? repairEnd - repairStart : 0
    const repairActive = !!inRepairAt
    timers.push({
      key: 'inc_repair',
      icon: 'fas fa-wrench',
      label: 'En Reparación',
      time: this.formatDuration(Math.max(repairMs, 0)),
      ms: Math.max(repairMs, 0),
      active: repairActive,
      isLive: incidence.Status === 'IN_REPAIR' || incidence.Status === 'REPAIR',
      severity: repairActive ? this.getTimerSeverity(Math.max(repairMs, 0)) : 'normal',
    })

    // 4) Total: sum of all 3 counters
    const totalMs = Math.max(openMs, 0) + Math.max(revisionMs, 0) + Math.max(repairMs, 0)
    timers.push({
      key: 'inc_total',
      icon: 'fas fa-hourglass-half',
      label: 'Total',
      time: this.formatDuration(totalMs),
      ms: totalMs,
      active: true,
      isLive: incidence.Status !== 'RESOLVED' && incidence.Status !== 'CLOSED',
      isTotal: true,
      severity: this.getTimerSeverity(totalMs),
    })

    return timers
  }

  getStatusCounts() {
    const { incidences } = this.props
    if (!incidences) return {}
    return incidences.reduce((acc, i) => {
      // Normalize REPAIR to IN_REPAIR for counting
      const status = i.Status === 'REPAIR' ? 'IN_REPAIR' : i.Status
      acc[status] = (acc[status] || 0) + 1
      return acc
    }, {})
  }

  getCategoryCounts() {
    const { incidences } = this.props
    if (!incidences) return {}
    return incidences.reduce((acc, i) => {
      acc[i.Category] = (acc[i.Category] || 0) + 1
      return acc
    }, {})
  }

  getFilteredIncidences() {
    const { incidences } = this.props
    const { activeFilter } = this.state
    if (!incidences) return []
    if (activeFilter === 'ALL') return incidences
    // Handle both REPAIR and IN_REPAIR
    if (activeFilter === 'IN_REPAIR') {
      return incidences.filter(i => i.Status === 'IN_REPAIR' || i.Status === 'REPAIR')
    }
    return incidences.filter(i => i.Status === activeFilter)
  }

  getKPIs() {
    const { incidences } = this.props
    if (!incidences) return { avgResponseTime: 0, avgRepairTime: 0, pendingCount: 0, resolvedToday: 0 }

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    let totalResponseTime = 0
    let responseTimeCount = 0
    let totalRepairTime = 0
    let repairTimeCount = 0
    let pendingCount = 0
    let resolvedToday = 0
    // SLA counters for repair-time SLA compliance (maxRepairTimeMax)
    let slaConsideredCount = 0
    let slaPassCount = 0

    incidences.forEach(inc => {
      // Count pending (not resolved/closed)
      if (inc.Status === 'OPEN' || inc.Status === 'IN_REVIEW' || inc.Status === 'IN_REPAIR' || inc.Status === 'REPAIR') {
        pendingCount++
      }

      // Count resolved today
      if ((inc.Status === 'RESOLVED' || inc.Status === 'CLOSED') && inc.ResolvedAt) {
        const resolvedDate = new Date(inc.ResolvedAt)
        if (resolvedDate >= todayStart) {
          resolvedToday++
        }
      }

      // Calculate response time (from creation to InReview or InRepair)
      if (inc.InReviewAt || inc.InRepairAt || inc.ResponseTime) {
        const created = new Date(inc.createdAt || inc.OpenAt)
        const responded = new Date(inc.InReviewAt || inc.InRepairAt || inc.ResponseTime)
        const diffHours = (responded - created) / (1000 * 60 * 60)
        if (diffHours > 0 && diffHours < 1000) {
          totalResponseTime += diffHours
          responseTimeCount++
        }
      }

      // Calculate repair time (from InRepair to Resolved)
      if ((inc.Status === 'RESOLVED' || inc.Status === 'CLOSED') && (inc.InRepairAt || inc.ResponseTime)) {
        const repairStart = new Date(inc.InRepairAt || inc.ResponseTime)
        const repairEnd = new Date(inc.ResolvedAt || inc.CloseTime)
        const diffHours = (repairEnd - repairStart) / (1000 * 60 * 60)
        if (diffHours > 0 && diffHours < 1000) {
          totalRepairTime += diffHours
          repairTimeCount++
        }
      }

      // SLA compliance for incidences: compare TOTAL elapsed time (Open → now/resolved) against SLA.maxRepairTimeMax
      // Include any incidence that has an assigned Customer.SLA (so KPI reflects *all* incidences with SLA)
      if (inc.Customer && inc.Customer.SLA && this.props.slas) {
        const slaId = inc.Customer.SLA && (inc.Customer.SLA._id || inc.Customer.SLA)
        const slaObj = (this.props.slas || []).find(s => String(s._id) === String(slaId))
        if (slaObj) {
          const priorityKey = (inc.Priority || 'MEDIUM').toLowerCase()
          const slaEntry = slaObj[priorityKey]
          const slaMax = slaEntry && slaEntry.maxRepairTimeMax
          if (typeof slaMax === 'number') {
            // reuse the existing timer calculation so SLA uses the same "total" definition shown in UI
            const timers = this.getIncidenceTimers(inc)
            const totalTimer = (timers || []).find(t => t.isTotal)
            if (totalTimer && typeof totalTimer.ms === 'number') {
              const elapsedHours = totalTimer.ms / (1000 * 60 * 60)
              if (elapsedHours >= 0 && elapsedHours < 2000) {
                slaConsideredCount++
                if (elapsedHours <= slaMax) slaPassCount++
              }
            }
          }
        }
      }
    })

    return {
      avgResponseTime: responseTimeCount > 0 ? Math.round(totalResponseTime / responseTimeCount * 10) / 10 : 0,
      avgRepairTime: repairTimeCount > 0 ? Math.round(totalRepairTime / repairTimeCount * 10) / 10 : 0,
      pendingCount,
      resolvedToday,
      // SLA KPI for incidences (repair time vs sla.maxRepairTimeMax)
      slaComplianceRatio: slaConsideredCount > 0 ? Math.round((slaPassCount / slaConsideredCount) * 1000) / 10 : null,
      slaConsideredCount,
      slaPassCount
    }
  }

  renderIncidenceCard(incidence) {
    const status = incidence.Status === 'REPAIR' ? 'IN_REPAIR' : incidence.Status
    const statusLabel = STATUS_LABELS[status] || status
    const categoryLabel = CATEGORY_LABELS[incidence.Category] || incidence.Category
    const priorityLabel = PRIORITY_LABELS[incidence.Priority] || incidence.Priority
    const timers = this.getIncidenceTimers(incidence)
    
    const openDate = new Date(incidence.createdAt || incidence.OpenAt)
    const formattedDate = `${openDate.getDate()}/${openDate.getMonth() + 1}/${openDate.getFullYear()}`
    const formattedTime = `${openDate.getHours().toString().padStart(2, '0')}:${openDate.getMinutes().toString().padStart(2, '0')}`

    return (
      <div key={incidence.number} className="inc-admin-card"
        onClick={() => this.goTo(incidence.number)}>
        <div className="inc-admin-card-row">
          <div className={`inc-admin-status-bar ${status}`}></div>
          <div className="inc-admin-card-content">
            {/* Incidence info */}
            <div className="inc-admin-info">
              <div className="inc-admin-number">
                Incidencia #{incidence.number}
              </div>
              <div className="inc-admin-meta">
                <span><i className="far fa-calendar"></i>{formattedDate}</span>
                <span><i className="far fa-clock"></i>{formattedTime}</span>
              </div>
            </div>

            {/* Customer + Machine */}
            <div className="inc-admin-context">
              <div className="inc-admin-context-item">
                <i className="fas fa-user"></i>
                <strong>{incidence.Customer ? incidence.Customer.Fullname : 'N/A'}</strong>
              </div>
              <div className="inc-admin-context-item">
                <i className="fas fa-desktop"></i>
                {incidence.Machine ? incidence.Machine.Serial : 'Todas las máquinas'}
              </div>
            </div>

            {/* Description */}
            <div className="inc-admin-desc">
              <div className={incidence.Description ? 'desc-text' : 'desc-empty'}>
                {incidence.Description || 'Sin descripción'}
              </div>
            </div>

            {/* Badges */}
            <div className="inc-admin-badges">
              <span className={`inc-status-badge ${status}`}>
                {statusLabel}
              </span>
              {incidence.Priority &&
                <span className={`inc-priority-badge ${incidence.Priority}`}>
                  {priorityLabel}
                </span>
              }
              <span className="badge badge-secondary" style={{ fontSize: 10 }}>
                {categoryLabel}
              </span>
            </div>

            {/* Time counters */}
            <div className="inc-admin-timers">
              {timers.map(t => (
                <div key={t.key}
                  className={`inc-timer-chip ${t.active ? t.severity : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                  <i className={t.icon}></i>
                  <span className="inc-timer-value">{t.active ? t.time : '--'}</span>
                </div>
              ))}

              {/* SLA remaining counter (show SLA - TOTAL elapsed time) */}
              {(() => {
                const slaRemMs = this.getSlaRemainingMsForIncidence(incidence)
                if (slaRemMs === null) return null
                const isPositive = slaRemMs > 0
                const display = isPositive ? this.formatDuration(slaRemMs) : `-${this.formatDuration(Math.abs(slaRemMs))}`
                const cls = `inc-timer-chip ${isPositive ? 'normal' : 'critical'}`
                return (
                  <div key="sla_rem" className={cls} title={isPositive ? 'Tiempo restante antes de SLA' : 'Tiempo pasado del SLA'}>
                    <i className="fas fa-bullseye"></i>
                    <span>{display}</span>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderDetailModal() {
    const { incidence, role, type } = this.props
    const { detailModalOpen, showActuationModal, actuationAction, actuationText } = this.state

    if (!detailModalOpen) return null

    const isLoading = !incidence
    const isAdmin = role === 'ADMIN'
    const isMaster = type === 'MASTER'
    const isResolved = incidence && incidence.Status === 'RESOLVED'

    return (
      <>
        <Modal isOpen={detailModalOpen} toggle={this.closeDetailModal}
          className="inc-detail-modal" size="xl">
          <ModalHeader toggle={this.closeDetailModal} className="inc-detail-modal-header">
            {incidence
              ? <span><i className="fas fa-exclamation-triangle"></i> Incidencia #{incidence.number}</span>
              : <span><i className="fas fa-exclamation-triangle"></i> Cargando...</span>
            }
          </ModalHeader>
          <ModalBody className="inc-detail-modal-body">
            {isLoading ? <LoadingProgress /> : (
              <>
                {/* Status strip: badge + timers in one row */}
                <div className="inc-modal-status-strip">
                  <div className="inc-modal-strip-badges">
                    <span className={`inc-status-badge ${incidence.Status === 'REPAIR' ? 'IN_REPAIR' : incidence.Status}`}>
                      <i className="fas fa-circle"></i>
                      {STATUS_LABELS[incidence.Status === 'REPAIR' ? 'IN_REPAIR' : incidence.Status] || incidence.Status}
                    </span>
                    {incidence.Priority &&
                      <span className={`inc-priority-badge ${incidence.Priority}`}>
                        <i className="fas fa-flag"></i>
                        {PRIORITY_LABELS[incidence.Priority] || incidence.Priority}
                      </span>
                    }
                  </div>
                  <div className="inc-modal-strip-separator"></div>
                  <div className="inc-modal-strip-timers">
                    {this.getIncidenceTimers(incidence).map(t => (
                      <div key={t.key}
                        className={`inc-modal-timer-item ${t.active ? t.severity : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                        <div className="inc-modal-timer-icon"><i className={t.icon}></i></div>
                        <div className="inc-modal-timer-value">{t.active ? t.time : '--'}</div>
                      </div>
                    ))}

                    {/* SLA remaining (modal) */}
                    {(() => {
                      const slaRemMs = this.getSlaRemainingMsForIncidence(incidence)
                      if (slaRemMs === null) return null
                      const isPositive = slaRemMs > 0
                      const label = isPositive ? 'Tiempo hasta SLA' : 'Tiempo sobre SLA'
                      const value = isPositive ? this.formatDuration(slaRemMs) : `-${this.formatDuration(Math.abs(slaRemMs))}`
                      const severity = isPositive ? 'normal' : 'critical'
                      return (
                        <div className={`inc-modal-timer-item ${severity}`} key="sla_modal">
                          <div className="inc-modal-timer-icon"><i className="fas fa-bullseye"></i></div>
                          <div className="inc-modal-timer-value"><div className="inc-modal-timer-label">{label}</div><div className="inc-modal-timer-number">{value}</div></div>
                        </div>
                      )
                    })() }
                  </div>
                </div>

                {/* Info grid */}
                <div className="inc-modal-detail-grid">
                  <div className="inc-detail-card">
                    <div className="inc-detail-card-title">
                      <i className="fas fa-info-circle"></i> Información General
                    </div>
                    <div className="inc-detail-card-body">
                      <div className="inc-detail-field">
                        <span className="inc-detail-field-label">Número</span>
                        <span className="inc-detail-field-value">#{incidence.number}</span>
                      </div>
                      <div className="inc-detail-field">
                        <span className="inc-detail-field-label">Categoría</span>
                        <span className="inc-detail-field-value">{CATEGORY_LABELS[incidence.Category] || incidence.Category}</span>
                      </div>
                      <div className="inc-detail-field">
                        <span className="inc-detail-field-label">Cliente</span>
                        <span className="inc-detail-field-value">{incidence.Customer && incidence.Customer.Fullname ? incidence.Customer.Fullname : 'N/A'}</span>
                      </div>
                      <div className="inc-detail-field">
                        <span className="inc-detail-field-label">Máquina</span>
                        <span className="inc-detail-field-value">{incidence.Machine ? incidence.Machine.Serial : 'N/A'}</span>
                      </div>
                      {incidence.SupportTicket &&
                        <div className="inc-detail-field">
                          <span className="inc-detail-field-label">Ticket Asociado</span>
                          <span className="inc-detail-field-value">#{incidence.SupportTicket.number}</span>
                        </div>
                      }
                    </div>
                  </div>

                  <div className="inc-detail-card">
                    <div className="inc-detail-card-title">
                      <i className="fas fa-calendar"></i> Fechas y Asignaciones
                    </div>
                    <div className="inc-detail-card-body">
                      <div className="inc-detail-field">
                        <span className="inc-detail-field-label">Fecha Apertura</span>
                        <span className="inc-detail-field-value">{new Date(incidence.createdAt || incidence.OpenAt).toLocaleString()}</span>
                      </div>
                      {incidence.OpenBy &&
                        <div className="inc-detail-field">
                          <span className="inc-detail-field-label">Abierta por</span>
                          <span className="inc-detail-field-value">{incidence.OpenBy.fullname}</span>
                        </div>
                      }
                      {incidence.InReviewAt &&
                        <div className="inc-detail-field">
                          <span className="inc-detail-field-label">En Revisión</span>
                          <span className="inc-detail-field-value">
                            {new Date(incidence.InReviewAt).toLocaleString()}
                            {incidence.InReviewBy ? ` — ${incidence.InReviewBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {incidence.InRepairAt &&
                        <div className="inc-detail-field">
                          <span className="inc-detail-field-label">En Reparación</span>
                          <span className="inc-detail-field-value">
                            {new Date(incidence.InRepairAt).toLocaleString()}
                            {incidence.InRepairBy ? ` — ${incidence.InRepairBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {incidence.ResolvedAt &&
                        <div className="inc-detail-field">
                          <span className="inc-detail-field-label">Resuelto</span>
                          <span className="inc-detail-field-value">
                            {new Date(incidence.ResolvedAt).toLocaleString()}
                            {incidence.ResolvedBy ? ` — ${incidence.ResolvedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="inc-detail-card" style={{ marginBottom: 16 }}>
                  <div className="inc-detail-card-title">
                    <i className="fas fa-file-alt"></i> Descripción
                  </div>
                  <div className="inc-detail-card-body">
                    <div className="inc-detail-description">
                      {incidence.Description || 'Sin descripción'}
                    </div>
                  </div>
                </div>

                {/* Repair Actuation */}
                {incidence.RepairActuation &&
                  <div className="inc-detail-card" style={{ marginBottom: 16 }}>
                    <div className="inc-detail-card-title">
                      <i className="fas fa-wrench"></i> Actuación de Reparación
                    </div>
                    <div className="inc-detail-card-body">
                      <div className="inc-detail-description">
                        {incidence.RepairActuation}
                      </div>
                      {incidence.RepairDetails && incidence.RepairDetails.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Notas de Reparación</div>
                          {incidence.RepairDetails.slice().reverse().map((note, i) => (
                            <div key={i} style={{ marginBottom: 10, padding: '8px 10px', background: '#f8f9fb', borderRadius: 6 }}>
                              <div style={{ fontSize: 12, color: '#666' }}>
                                {note.InRepairBy && note.InRepairBy.fullname ? note.InRepairBy.fullname : ''}
                                {note.CreatedAt ? ` — ${new Date(note.CreatedAt).toLocaleString()}` : ''}
                              </div>
                              <div style={{ marginTop: 6 }}>{note.Text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                }

                {/* Resolved Actuation */}
                {incidence.ResolvedActuation &&
                  <div className="inc-detail-card" style={{ marginBottom: 16 }}>
                    <div className="inc-detail-card-title">
                      <i className="fas fa-check-circle"></i> Actuación de Resolución
                    </div>
                    <div className="inc-detail-card-body">
                      <div className="inc-detail-description">
                        {incidence.ResolvedActuation}
                      </div>
                    </div>
                  </div>
                }

                {/* Actions - Workflow buttons */}
                {isAdmin && !isResolved &&
                  <div className="inc-detail-actions-bar">
                    <h3><i className="fas fa-cogs"></i> Acciones</h3>
                    <div className="inc-detail-actions-row justify-content-center">
                      {/* OPEN -> only Review button */}
                      {incidence.Status === 'OPEN' &&
                        <button className="inc-detail-action-btn review"
                          onClick={() => this.handleReviewIncidence(incidence.number)}>
                          <i className="fas fa-eye"></i>Revisar
                        </button>
                      }
                      {/* IN_REVIEW -> Repair or Resolve */}
                      {incidence.Status === 'IN_REVIEW' &&
                        <>
                          <button className="inc-detail-action-btn repair"
                            onClick={() => this.handleOpenRepairModal(incidence.number)}>
                            <i className="fas fa-wrench"></i>Pasar a Reparación
                          </button>
                          <button className="inc-detail-action-btn resolve"
                            onClick={() => this.handleOpenResolveModal(incidence.number)}>
                            <i className="fas fa-check"></i>Resolver
                          </button>
                        </>
                      }
                      {/* IN_REPAIR -> Resolve */}
                      {(incidence.Status === 'IN_REPAIR' || incidence.Status === 'REPAIR') &&
                        <>
                          <button className="inc-detail-action-btn add-note"
                            onClick={() => this.handleOpenAppendRepairModal(incidence.number)}>
                            <i className="fas fa-sticky-note"></i>Añadir nota
                          </button>
                          <button className="inc-detail-action-btn resolve"
                            onClick={() => this.handleOpenResolveModal(incidence.number)}>
                            <i className="fas fa-check"></i>Resolver
                          </button>
                        </>
                      }
                    </div>
                  </div>
                }
              </>
            )}
          </ModalBody>
        </Modal>

        {/* Actuation Modal for Repair/Resolve */}
        <Modal isOpen={showActuationModal} toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
          <ModalHeader toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
            {actuationAction === 'repair' ? 'Pasar a Reparación' : 'Resolver Incidencia'}
          </ModalHeader>
          <ModalBody>
            <p>
              {actuationAction === 'repair' 
                ? 'Describe la actuación de reparación:' 
                : 'Describe la actuación realizada para resolver la incidencia:'}
            </p>
            <textarea
              className="form-control"
              rows={4}
              value={actuationText}
              onChange={(e) => this.setState({ actuationText: e.target.value })}
              placeholder="Escribe la actuación realizada..."
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleActuationSubmit}>
              {actuationAction === 'repair' ? 'Pasar a Reparación' : 'Resolver'}
            </Button>
            <Button color="secondary" onClick={() => this.setState({ showActuationModal: false, actuationText: '' })}>
              Cancelar
            </Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  renderSummaryPanel() {
    const { incidences } = this.props
    const counts = this.getStatusCounts()
    const categoryCounts = this.getCategoryCounts()
    const total = incidences ? incidences.length : 0

    return (
      <div className="inc-admin-summary-panel">
        {/* Status summary */}
        <div className="inc-summary-card">
          <div className="inc-summary-card-header">
            <h3><i className="fas fa-chart-pie"></i> Resumen por Estado</h3>
          </div>
          <div className="inc-summary-card-body">
            <div className="inc-summary-total">{total} incidencias totales</div>
            <div className="inc-summary-table">
              {['OPEN', 'IN_REVIEW', 'IN_REPAIR', 'RESOLVED'].map(status => {
                const count = counts[status] || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                return (
                  <div className="inc-summary-row" key={status}
                    onClick={() => this.setState({ activeFilter: status })}>
                    <div className="inc-summary-row-left">
                      <span className={`inc-summary-dot ${status}`}></span>
                      <span className="inc-summary-label">{STATUS_LABELS[status]}</span>
                    </div>
                    <div className="inc-summary-row-right">
                      <span className="inc-summary-count">{count}</span>
                      <div className="inc-summary-bar-bg">
                        <div className={`inc-summary-bar-fill ${status}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="inc-summary-pct">{pct}%</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Category summary */}
        <div className="inc-summary-card">
          <div className="inc-summary-card-header">
            <h3><i className="fas fa-tags"></i> Por Categoría</h3>
          </div>
          <div className="inc-summary-card-body">
            <div className="inc-summary-table">
              {Object.entries(categoryCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => (
                  <div className="inc-summary-row" key={cat}>
                    <div className="inc-summary-row-left">
                      <span className="inc-summary-label">{CATEGORY_LABELS[cat] || cat}</span>
                    </div>
                    <div className="inc-summary-row-right">
                      <span className="inc-summary-count">{count}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { role, incidences, error, loading } = this.props
    const { activeFilter } = this.state
    const counts = this.getStatusCounts()
    const filteredIncidences = this.getFilteredIncidences()
    const kpis = this.getKPIs()

    return (
      <div className="incidences-admin-view">
        {/* Header */}
        <div className="inc-admin-header">
          <div className="header-content">
            <div className="header-text">
              <h1>
                <i className="fas fa-exclamation-triangle mr-3"></i>
                Incidencias
              </h1>
            </div>
            <div className="header-actions">
              {role === 'ADMIN' &&
                <Link to="/incidences/create">
                  <button type="button" className="btn bg-vw-light text-white mr-2">
                    <i className="fas fa-plus mr-1"></i>Nueva
                  </button>
                </Link>
              }
              <button className="inc-admin-download-btn" onClick={this.handleIncidencesCSV}>
                <i className="fas fa-file-excel"></i>Descargar CSV
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="incidence-kpi-row">
          <div className="incidence-kpi-card pending">
            <div className="kpi-icon"><i className="fas fa-clock"></i></div>
            <div className="kpi-content">
              <div className="kpi-value">{kpis.pendingCount}</div>
              <div className="kpi-label">Pendientes</div>
            </div>
          </div>
          <div className="incidence-kpi-card resolved">
            <div className="kpi-icon"><i className="fas fa-check-circle"></i></div>
            <div className="kpi-content">
              <div className="kpi-value">{kpis.resolvedToday}</div>
              <div className="kpi-label">Resueltas Hoy</div>
            </div>
          </div>
          <div className="incidence-kpi-card response">
            <div className="kpi-icon"><i className="fas fa-bolt"></i></div>
            <div className="kpi-content">
              <div className="kpi-value">{kpis.slaComplianceRatio === null ? '—' : `${kpis.slaComplianceRatio}%`}{kpis.slaConsideredCount && kpis.slaConsideredCount > 0 ? ` (${kpis.slaPassCount}/${kpis.slaConsideredCount})` : ''}</div>
              <div className="kpi-label">Ratio de cumplimiento de SLA</div>
            </div>
          </div>
          <div className="incidence-kpi-card repair">
            <div className="kpi-icon"><i className="fas fa-wrench"></i></div>
            <div className="kpi-content">
              <div className="kpi-value">{kpis.avgRepairTime}h</div>
              <div className="kpi-label">Tiempo Reparación Medio</div>
            </div>
          </div>
        </div>

        {/* Split layout - 2 columns */}
        <div className="inc-admin-split">
          {/* LEFT — Filter + Incidence List */}
          <div className="inc-admin-list-panel">
            {/* Filter */}
            <IncidencesFilterForm />
            
            <div className="inc-filter-tabs">
              {FILTER_TABS.map(tab => (
                <button key={tab.key}
                  className={`inc-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                  onClick={() => this.setState({ activeFilter: tab.key })}>
                  {tab.label}
                  {tab.key !== 'ALL' && counts[tab.key] > 0 &&
                    <span style={{ marginLeft: 4 }}>({counts[tab.key]})</span>
                  }
                </button>
              ))}
            </div>

            {error ? (
              <ErrorMessage message={error.message} />
            ) : !incidences || loading ? (
              <LoadingProgress />
            ) : (
              <div className="inc-admin-list">
                {filteredIncidences.length > 0
                  ? filteredIncidences.map(incidence => this.renderIncidenceCard(incidence))
                  : (
                    <div className="inc-admin-empty">
                      <i className="fas fa-inbox"></i>
                      <p>No hay incidencias {activeFilter !== 'ALL' ? `con estado "${STATUS_LABELS[activeFilter]}"` : ''}</p>
                    </div>
                  )
                }
              </div>
            )}
          </div>

          {/* RIGHT — Summary Panel */}
          {incidences && !loading && this.renderSummaryPanel()}
        </div>

        {/* Incidence detail modal */}
        {this.renderDetailModal()}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  incidences: state.incidences.incidences,
  incidence: state.incidences.incidence,
  error: state.incidences.error,
  loading: state.incidences.loading,
  role: state.auth.role,
  type: state.auth.type,
  form: state.form,
  incidenceNeedRefresh: state.incidences.incidenceNeedRefresh,
  slas: state.slas.slas
})

const mapDispatchToProps = dispatch => ({
  fetchIncidences: bindActionCreators(fetchIncidences, dispatch),
  fetchIncidence: bindActionCreators(fetchIncidence, dispatch),
  fetchIncidencesCSV: bindActionCreators(fetchIncidencesCSV, dispatch),
  reviewIncidence: bindActionCreators(reviewIncidence, dispatch),
  repairIncidence: bindActionCreators(repairIncidence, dispatch),
  appendRepairDetail: bindActionCreators(appendRepairDetail, dispatch),
  resolveIncidence: bindActionCreators(resolveIncidence, dispatch),
  fetchSLAs: bindActionCreators(require('../actions/slas').fetchSLAs, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(IncidencesView)

